import { authRedirectRoute, Guard } from '@if/mui'
import { access } from '@src/access'
import TemplateGlobal from '@src/components/Layout/TemplateGlobal'
import { useDevTools } from '@src/types/constants'
import type { JSX } from 'react'
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router-dom'
import DevToolsPage from './Admin/DevToolsPage'
import TransactionRetryPage from './Admin/TransactionRetryPage'
import CompanyRoute, { CompanyRedirect } from './CompanyRoute'
import BankJobPage from './Disburse/BankJobPage'
import DisbursePage from './Disburse/DisbursePage'
import InteracHistoryListPage from './InteracHistory/InteracHistoryListPage'
import InteracReconciliationPage from './InteracWire/InteracReconciliationPage'
import LoanCalendarPage from './Loans/LoanCalendarPage'
import LoanCreatePage from './Loans/LoanCreatePage'
import LoanListPage from './Loans/LoanListPage'

// The whole URL surface in one tree. Navigate targets are absolute so they read the same
// from here as they do from the call site.
const pages: RouteObject[] = [
  { path: '/', element: <CompanyRedirect to="loans" /> },
  // Every screen that depends on a financing company carries it first, as the API routes do, so a link opens
  // in the company it was made in whatever the reader worked in last. ':tab?' is the PageTabs segment — without
  // it the strip's URLs stop matching; the bare path renders the first tab.
  {
    path: '/:company',
    element: <CompanyRoute />,
    children: [
      { path: 'loans', element: <LoanListPage /> },
      { path: 'loans/:id/:tab?', element: <LoanCalendarPage /> },
      { path: 'disbursements/:tab?', element: <DisbursePage /> },
      // One deposit of the history tab; deeper than the tab route, so no clash with it.
      { path: 'disbursements/history/:bankJobId', element: <BankJobPage /> },
      { path: 'admin/transaction-retry/:tab?', element: <TransactionRetryPage /> },
      { path: '*', element: <CompanyRedirect to="loans" /> },
    ],
  },
  // The company-less URLs of the screens above, kept so bookmarks still land somewhere.
  {
    path: '/loans',
    children: [
      { index: true, element: <CompanyRedirect to="loans" /> },
      ...(useDevTools ? [{ path: 'create', element: <LoanCreatePage /> }] : []),
      { path: '*', element: <CompanyRedirect to="loans" /> },
    ],
  },
  { path: '/disbursements/*', element: <CompanyRedirect to="disbursements" /> },
  {
    path: '/interac-wire',
    children: [
      { index: true, element: <InteracReconciliationPage /> },
      { path: '*', element: <Navigate to="/interac-wire" replace /> },
    ],
  },
  { path: '/interac-history', element: <InteracHistoryListPage /> },
  {
    path: '/admin',
    children: [
      { index: true, element: <CompanyRedirect to="admin/transaction-retry" /> },
      { path: 'transaction-retry/*', element: <CompanyRedirect to="admin/transaction-retry" /> },
      {
        path: 'dev-tools',
        element: (
          <Guard {...access.openDevTools} accessRedirect="/admin">
            <DevToolsPage />
          </Guard>
        ),
      },
      { path: '*', element: <Navigate to="/admin" replace /> },
    ],
  },
]

// https://reactrouter.com/6.30.1/routers/picking-a-router
const router = createBrowserRouter([
  { element: <TemplateGlobal />, children: pages },
  // Outside the layout, so the IdP landing (`/auth-page`) resolves without mounting the chrome.
  authRedirectRoute,
  { path: '*', element: <Navigate to="/" replace /> },
])

const Router = (): JSX.Element => {
  return <RouterProvider router={router} />
}

export default Router
